/**
 * Global memory governor.
 *
 * Observes total memory usage against a user-configured limit and, as the
 * pressure level rises, fires user-visible warnings, runs registered
 * pressure-relief handlers, and gates new top-level geometry creation.
 *
 * The governor is a single coordinator wired around primitives that already
 * exist in the app: `performance.memory`, `getWasmMainHeapBytes()`,
 * `clearModifierCache()`, `clearMaterialLibraryCache()`,
 * `undoManager.setMaxBytes()`, BVH cancel, and `showToast()`. It owns no
 * memory-bearing state of its own — only the state machine.
 *
 * Levels with hysteresis (so ordinary GC churn does not flap):
 *
 *   nominal     <70% of limit
 *   warn        ≥80% (clears once usage drops below 70% for N samples)
 *   critical    ≥90% (clears once usage drops below 80% for N samples)
 *   over        ≥100% (clears once usage drops below 90% for N samples)
 *
 * The `Off` setting (limit === 0) disables sampling, handlers, and the gate.
 */

import { dlog } from "../debug/debugLogger.ts";
import { showToast } from "../ui/toastBus.ts";
import { getWasmMainHeapBytes } from "../wasm/init.ts";

// ---------------------------------------------------------------------------
// Types & constants
// ---------------------------------------------------------------------------

export type PressureLevel = "nominal" | "warn" | "critical" | "over";

export interface MemorySample {
  /** Estimated total resident bytes (JS heap + WASM main-thread linear memory). */
  usedBytes: number;
  /** Configured limit, or 0 if disabled. */
  limitBytes: number;
  /** Current pressure level. */
  level: PressureLevel;
}

export interface PressureHandler {
  id: string;
  /** Lower runs first. Convention: 10s = least disruptive, 40s = most. */
  priority: number;
  /** Short human description for diagnostics. */
  description: string;
  /** Returns best-effort estimate of bytes freed (informational only). */
  run: () => { freedBytes: number };
}

const MB = 1024 * 1024;

/** Sampler cadence. Heap APIs only update ~1 Hz in Chromium; 2 Hz is plenty. */
const SAMPLE_INTERVAL_MS = 2000;

/** Re-run handler chain at most this often while still critical. */
const HANDLER_RECHECK_MS = 5000;

// Enter thresholds (fraction of limit).
const ENTER_WARN = 0.8;
const ENTER_CRITICAL = 0.9;
const ENTER_OVER = 1.0;

// Exit thresholds — wider gap creates hysteresis, immune to GC noise.
const EXIT_WARN = 0.7;
const EXIT_CRITICAL = 0.8;
const EXIT_OVER = 0.9;

/** Consecutive samples below the exit threshold required to downgrade. */
const DOWNGRADE_DEBOUNCE = 3;

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

const handlers: PressureHandler[] = [];
const subscribers = new Set<(s: MemorySample) => void>();

let limitBytes = 0;
let level: PressureLevel = "nominal";
let usedBytes = 0;
let timer: ReturnType<typeof setInterval> | null = null;
let lastHandlerRunAt = 0;
let exitDebounceCount = 0;
/**
 * Optional opener for the Performance settings tab. Wired by App.tsx via
 * `setSettingsOpener`; the governor itself does not import the store. When
 * non-null, level-change toasts include a CTA that invokes this callback
 * so the user can adjust the memory limit in one click.
 */
let settingsOpener: (() => void) | null = null;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Start the governor. Idempotent — repeated calls are no-ops. Returns a
 * disposer that stops sampling and clears state.
 */
export function startMemoryGovernor(): () => void {
  if (timer != null) return stopMemoryGovernor;
  scheduleTimer();
  return stopMemoryGovernor;
}

export function stopMemoryGovernor(): void {
  if (timer != null) {
    clearInterval(timer);
    timer = null;
  }
  level = "nominal";
  exitDebounceCount = 0;
  notify();
}

/**
 * Update the configured limit. `0` disables the governor entirely (no
 * sampling, no toasts, no gate). Called from the store's `setMemoryLimit`.
 */
export function setMemoryLimit(bytes: number): void {
  const next = Math.max(0, Math.floor(bytes));
  if (next === limitBytes) return;
  limitBytes = next;
  if (limitBytes === 0) {
    if (timer != null) {
      clearInterval(timer);
      timer = null;
    }
    level = "nominal";
    exitDebounceCount = 0;
    notify();
    return;
  }
  // Non-zero limit: ensure timer is running and re-evaluate immediately so the
  // user sees the effect of the change without waiting for the next tick.
  scheduleTimer();
  sample();
}

export function getMemorySample(): MemorySample {
  return { usedBytes, limitBytes, level };
}

export function subscribeMemory(cb: (s: MemorySample) => void): () => void {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}

/**
 * Wire the callback that opens the Performance settings tab. Pass null to
 * unregister. Called at app boot — keeps `memoryGovernor.ts` from importing
 * the store, and lets tests run without a UI shell.
 */
export function setSettingsOpener(fn: (() => void) | null): void {
  settingsOpener = fn;
}

/**
 * Display a memory-related toast with the standard "Open Settings" CTA
 * appended. Centralized so every site that surfaces a memory error
 * (governor level changes, gate refusals, import refusals) gets identical
 * styling, the same one-click escape hatch into the Memory Limit setting,
 * and consistent dedup behaviour.
 */
export function showMemoryToast(
  message: string,
  opts: {
    severity: "warning" | "error";
    duration?: number;
    dedupKey: string;
  },
): void {
  showToast(message, {
    severity: opts.severity,
    duration: opts.duration,
    dedupKey: opts.dedupKey,
    action: settingsOpener
      ? { label: "Open Settings", onClick: settingsOpener }
      : undefined,
  });
}

/**
 * Register a pressure handler. Returns a disposer for symmetry with
 * `subscribeMemory`. Handlers added after the governor has already fired
 * relief are simply included in the next round.
 */
export function registerPressureHandler(h: PressureHandler): () => void {
  handlers.push(h);
  handlers.sort((a, b) => a.priority - b.priority);
  return () => {
    const idx = handlers.indexOf(h);
    if (idx >= 0) handlers.splice(idx, 1);
  };
}

/**
 * Geometry-add gate. Returns true if the proposed allocation is admissible.
 *
 * Semantics:
 *   - limit disabled (`Off`)         → always admit
 *   - level === "over"               → refuse
 *   - usedBytes + estBytes > limit   → refuse (would push us over)
 *   - otherwise                      → admit
 *
 * `estBytes` is an advisory pre-emption hint; pass 0 to test only the current
 * level. Refusals are silent — callers are expected to surface their own
 * user-facing message (most go through `addSceneObject`'s policy path which
 * fires a deduplicated toast centrally).
 */
export function canAllocate(estBytes: number): boolean {
  if (limitBytes === 0) return true;
  if (level === "over") return false;
  if (usedBytes + Math.max(0, estBytes) > limitBytes) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Sampling
// ---------------------------------------------------------------------------

function scheduleTimer(): void {
  if (timer != null) return;
  if (limitBytes === 0) return;
  timer = setInterval(sample, SAMPLE_INTERVAL_MS);
}

/** Test-only override; null in production. See `_setReadUsedBytesForTest`. */
let testReadOverride: (() => number) | null = null;

function readUsedBytes(): number {
  if (testReadOverride) return testReadOverride();

  let used = 0;
  if (typeof performance !== "undefined") {
    const m = (
      performance as unknown as {
        memory?: {
          usedJSHeapSize: number;
          totalJSHeapSize: number;
          jsHeapSizeLimit: number;
        };
      }
    ).memory;
    if (m && Number.isFinite(m.usedJSHeapSize)) used = m.usedJSHeapSize;
  }
  // Fallback for browsers without performance.memory (e.g. Firefox): pull from
  // the per-frame stats published by Viewport3D, when the viewport is mounted.
  if (used === 0 && typeof window !== "undefined") {
    const stats = window.__poseref_frameStats;
    if (stats?.jsHeapUsed && Number.isFinite(stats.jsHeapUsed)) {
      used = stats.jsHeapUsed;
    }
  }
  used += getWasmMainHeapBytes();
  return used;
}

function sample(): void {
  if (limitBytes === 0) return;
  usedBytes = readUsedBytes();
  applyRatio(usedBytes / limitBytes);

  // While critical (or over), re-run pressure relief on a cadence.
  if ((level === "critical" || level === "over") && handlers.length > 0) {
    const now = Date.now();
    if (now - lastHandlerRunAt >= HANDLER_RECHECK_MS) {
      lastHandlerRunAt = now;
      runPressureRelief();
    }
  }

  notify();
}

/**
 * Step the state machine for the given ratio. Upgrades are immediate.
 * Downgrades require `DOWNGRADE_DEBOUNCE` consecutive samples below the
 * exit threshold so ordinary GC churn (30–80 MB swings) does not flap the
 * level. May fire `onLevelChange` once per call if the level transitions.
 */
function applyRatio(ratio: number): void {
  const prev = level;
  const next = decideNextLevel(prev, ratio);
  if (next !== prev) {
    level = next;
    exitDebounceCount = 0;
    onLevelChange(prev, next);
  }
}

/**
 * Pure-ish state-machine step. Mutates `exitDebounceCount` (the only state
 * a hysteretic downgrade strictly requires); everything else is determined
 * by `(prev, ratio)`. Returns the new level — equal to `prev` when the
 * downgrade debounce hasn't elapsed.
 */
function decideNextLevel(prev: PressureLevel, ratio: number): PressureLevel {
  // Upgrades are strictly monotonic and immediate — no debounce.
  if (ratio >= ENTER_OVER) return "over";
  if (prev === "nominal" && ratio >= ENTER_WARN) {
    return ratio >= ENTER_CRITICAL ? "critical" : "warn";
  }
  if (prev === "warn" && ratio >= ENTER_CRITICAL) return "critical";

  // Downgrades: bump the debounce count while below the current level's exit
  // threshold; once we reach DOWNGRADE_DEBOUNCE, drop directly to whichever
  // level the current ratio falls inside (may skip levels on a sharp drop).
  const exitThreshold = exitThresholdFor(prev);
  if (exitThreshold === null) return prev; // nominal — nowhere lower to go.
  if (ratio >= exitThreshold) {
    exitDebounceCount = 0;
    return prev;
  }
  if (++exitDebounceCount < DOWNGRADE_DEBOUNCE) return prev;
  if (ratio >= ENTER_CRITICAL) return "critical";
  if (ratio >= ENTER_WARN) return "warn";
  return "nominal";
}

function exitThresholdFor(level: PressureLevel): number | null {
  switch (level) {
    case "over":
      return EXIT_OVER;
    case "critical":
      return EXIT_CRITICAL;
    case "warn":
      return EXIT_WARN;
    case "nominal":
      return null;
  }
}

// ---------------------------------------------------------------------------
// Level-change side effects
// ---------------------------------------------------------------------------

function onLevelChange(prev: PressureLevel, next: PressureLevel): void {
  dlog.info(
    "js",
    "memory.governor.level",
    `${prev} → ${next} (${fmtBytes(usedBytes)} / ${fmtBytes(limitBytes)})`,
    { prev, next, usedBytes, limitBytes },
  );

  switch (next) {
    case "warn":
      showMemoryToast(
        `Memory usage is high (${pct(usedBytes, limitBytes)} of ${fmtBytes(limitBytes)}). Consider saving and closing unused objects.`,
        { severity: "warning", duration: 6000, dedupKey: "memory-warn" },
      );
      break;
    case "critical":
      // Run handlers immediately — don't wait for the recheck cadence.
      lastHandlerRunAt = Date.now();
      runPressureRelief();
      showMemoryToast(
        `Memory usage is critical (${pct(usedBytes, limitBytes)} of ${fmtBytes(limitBytes)}). Caches were cleared to free space.`,
        { severity: "warning", duration: 6000, dedupKey: "memory-critical" },
      );
      break;
    case "over":
      showMemoryToast(
        `Memory limit reached. New geometry is blocked — remove objects, undo, or raise the limit in Settings.`,
        { severity: "error", duration: 8000, dedupKey: "memory-over" },
      );
      break;
    case "nominal":
      // No toast — silent recovery is the best UX.
      break;
  }
}

function runPressureRelief(): void {
  if (handlers.length === 0) return;
  let totalFreed = 0;
  let ranCount = 0;
  for (const h of handlers) {
    let freedBytes = 0;
    try {
      ({ freedBytes } = h.run());
    } catch (err) {
      dlog.info("js", "memory.governor.handler.error", `${h.id} threw`, {
        id: h.id,
        error: err instanceof Error ? err.message : String(err),
      });
      continue;
    }
    ranCount++;
    totalFreed += freedBytes;
    dlog.info(
      "js",
      "memory.governor.handler.ran",
      `${h.id} freed ~${fmtBytes(freedBytes)}`,
      { id: h.id, freedBytes },
    );
    // Re-sample to see whether we can stop early. This costs nothing —
    // performance.memory is O(1) — and avoids running disruptive handlers
    // (e.g. trim-undo) when the lighter ones already relieved pressure.
    usedBytes = readUsedBytes();
    if (usedBytes / limitBytes < EXIT_CRITICAL) break;
  }
  dlog.info(
    "js",
    "memory.governor.relief.done",
    `ran ${ranCount} handler(s), freed ~${fmtBytes(totalFreed)}, now ${pct(usedBytes, limitBytes)}`,
    { ranCount, totalFreed, usedBytes, limitBytes },
  );
}

// ---------------------------------------------------------------------------
// Notification
// ---------------------------------------------------------------------------

function notify(): void {
  if (subscribers.size === 0) return;
  const snap: MemorySample = { usedBytes, limitBytes, level };
  for (const cb of subscribers) {
    try {
      cb(snap);
    } catch {
      /* observer threw — do not let it derail the governor */
    }
  }
}

// ---------------------------------------------------------------------------
// Test helpers (not exported via public surface; consumed by unit tests only)
// ---------------------------------------------------------------------------

/**
 * Reset all governor state. Used by tests; not for production code paths.
 */
export function _resetForTest(): void {
  if (timer != null) {
    clearInterval(timer);
    timer = null;
  }
  handlers.length = 0;
  subscribers.clear();
  limitBytes = 0;
  level = "nominal";
  usedBytes = 0;
  lastHandlerRunAt = 0;
  exitDebounceCount = 0;
  testReadOverride = null;
  settingsOpener = null;
}

/**
 * Override the underlying heap reader. Used by tests that exercise the
 * inter-handler re-sample logic in `runPressureRelief`. Pass null to clear.
 */
export function _setReadUsedBytesForTest(fn: (() => number) | null): void {
  testReadOverride = fn;
}

/**
 * Drive the sampler with a fake usedBytes value. Used by tests; not for
 * production code paths.
 */
export function _stepForTest(fakeUsedBytes: number): MemorySample {
  if (limitBytes === 0) return getMemorySample();
  usedBytes = fakeUsedBytes;
  applyRatio(usedBytes / limitBytes);
  if ((level === "critical" || level === "over") && handlers.length > 0) {
    const now = Date.now();
    if (now - lastHandlerRunAt >= HANDLER_RECHECK_MS) {
      lastHandlerRunAt = now;
      runPressureRelief();
    }
  }
  notify();
  return getMemorySample();
}

/**
 * Force the next handler-relief run on the next `_stepForTest` call by
 * resetting the recheck cooldown. Used by tests that want to assert
 * handler behaviour without waiting `HANDLER_RECHECK_MS`.
 */
export function _allowHandlersForTest(): void {
  lastHandlerRunAt = 0;
}

// ---------------------------------------------------------------------------
// Memory-limit options surfaced in Settings
// ---------------------------------------------------------------------------

/**
 * Default chosen with iOS Safari (the primary deployment target) in mind.
 * iPhones kill tabs around ~1 GB total RSS, iPads a bit more. Setting the
 * limit to 1 GB would only fire the 80% warn at ~820 MB — barely earlier
 * than the OS itself. 512 MB warns at ~410 MB, giving the user enough
 * headroom to act (close objects, undo, or opt into a higher cap on
 * desktop). Power users can raise to 1–4 GB via Settings.
 */
export const MEMORY_LIMIT_DEFAULT_BYTES = 512 * MB;

/**
 * Allowed values surfaced in the Performance tab. `0` disables the governor;
 * matches the shape of `UNDO_BUDGET_OPTIONS` in `storeUndo.ts`.
 */
export const MEMORY_LIMIT_OPTIONS: ReadonlyArray<{
  label: string;
  bytes: number;
}> = [
  { label: "Off", bytes: 0 },
  { label: "512 MB", bytes: 512 * MB },
  { label: "1 GB", bytes: 1024 * MB },
  { label: "2 GB", bytes: 2 * 1024 * MB },
  { label: "4 GB", bytes: 4 * 1024 * MB },
];

const MEMORY_LIMIT_VALUES = new Set(MEMORY_LIMIT_OPTIONS.map((o) => o.bytes));

/** Coerce an arbitrary input to one of the segmented options. */
export function clampMemoryLimit(bytes: number): number {
  if (!Number.isFinite(bytes) || bytes < 0) return MEMORY_LIMIT_DEFAULT_BYTES;
  if (MEMORY_LIMIT_VALUES.has(bytes)) return bytes;
  // Snap to nearest legal option for forwards-compat with future option lists.
  let best = MEMORY_LIMIT_DEFAULT_BYTES;
  let bestDelta = Number.POSITIVE_INFINITY;
  for (const o of MEMORY_LIMIT_OPTIONS) {
    const d = Math.abs(o.bytes - bytes);
    if (d < bestDelta) {
      bestDelta = d;
      best = o.bytes;
    }
  }
  return best;
}

// ---------------------------------------------------------------------------
// Local formatters
// ---------------------------------------------------------------------------

function fmtBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < MB) return `${(b / 1024).toFixed(0)} KB`;
  if (b < 1024 * MB) return `${(b / MB).toFixed(0)} MB`;
  return `${(b / (1024 * MB)).toFixed(2)} GB`;
}

function pct(used: number, limit: number): string {
  if (limit <= 0) return "—";
  return `${Math.round((used / limit) * 100)}%`;
}
